import React from 'react'
import { Animated, Easing, PanResponder, Platform, Text, View } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import Svg, { Ellipse, G, Line, Text as SvgText, TSpan } from 'react-native-svg'
import {
  chooseHomePull, homePullDistance, startsHomePull, stepHomeWheel, EMPTY_HOME_PULL_SESSION, HOME_PULL_REFRESH_DISTANCE, HOME_WHEEL_END_MS,
  IDLE_HOME_WHEEL, type HomePullBackdrop, type HomePullSession,
} from '../../lib/homePullSecret'
import { reportError } from '../../lib/reporting'

function SecretBackdrop({ kind, height }: { kind: HomePullBackdrop; height: number }) {
  return <Svg width="100%" height="100%" viewBox={`0 0 400 ${height}`} preserveAspectRatio="xMidYMin slice">
    {kind === 'wormhole' ? Array.from({ length: 12 }, (_, n) => <Ellipse key={n} cx={200} cy={75}
      rx={14 + n * 22} ry={7 + n * 11} stroke={n % 2 ? '#91ad68' : '#6573a3'} strokeOpacity={0.12 + n / 55} strokeWidth={2} fill="none" rotation={-18} origin="200,75" />)
      : kind === 'matrix' ? Array.from({ length: 15 }, (_, n) => <SvgText key={n} x={n * 29} y={-25 + n % 4 * 10} fontSize={12} fontFamily="monospace" fill="#8ac56a">
        {Array.from({ length: Math.ceil(height / 15) + 4 }, (_, row) => <TSpan key={row} x={n * 29} dy={15} fillOpacity={0.08 + row % 4 * 0.05}>
          {(n * 13 + row * 7) % 3 ? '1' : '0'}
        </TSpan>)}
      </SvgText>) : <G stroke="#8a9b55" strokeOpacity={0.14}>
        {Array.from({ length: 16 }, (_, n) => <Line key={`x${n}`} x1={n * 28} y1={0} x2={n * 28} y2={height} />)}
        {Array.from({ length: Math.ceil(height / 28) + 1 }, (_, n) => <Line key={`y${n}`} x1={0} y1={n * 28} x2={400} y2={n * 28} />)}
      </G>}
  </Svg>
}

/** DOM-only check (web): is the element under the wheel, and every scroller above it, at the top? */
function webAtTop(target: EventTarget | null, root: Element) {
  for (let el = target as Element | null; el && el !== root; el = el.parentElement) {
    if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight) return false
  }
  return true
}

/**
 * Wraps the whole home page below the status bar, header included. The
 * pull-to-refresh gesture at the top of the page (a drag down, or on web a
 * wheel/trackpad scroll up, while the list is already at offset 0) slides the
 * page down to uncover the secret, only while held. Letting go past the
 * refresh distance refreshes the lists: the pull takes the gesture from the
 * list, so it does the RefreshControl's job.
 */
export function HomePullReveal({ children, scrollTop, onRefresh }: { children: React.ReactNode; scrollTop: React.RefObject<number>; onRefresh: () => Promise<unknown> }) {
  const reducedMotion = useReducedMotion()
  const translate = React.useRef(new Animated.Value(0)).current
  const session = React.useRef<HomePullSession>(EMPTY_HOME_PULL_SESSION)
  const [secret, setSecret] = React.useState<ReturnType<typeof chooseHomePull> | null>(null)
  const [backdropHeight, setBackdropHeight] = React.useState(600)
  const root = React.useRef<View>(null)
  const heldDistance = React.useRef(0)
  const held = React.useRef(false)
  const refreshRef = React.useRef(onRefresh); refreshRef.current = onRefresh
  const reducedRef = React.useRef(reducedMotion); reducedRef.current = reducedMotion
  const pull = React.useMemo(() => ({
    begin() {
      translate.stopAnimation(); held.current = true
      const choice = chooseHomePull(session.current); session.current = choice.session; setSecret(choice)
    },
    move(distance: number) {
      heldDistance.current = distance
      translate.setValue(distance)
    },
    /** Springs back; refreshes when let go (not cancelled) past the refresh distance. */
    end(letGo: boolean) {
      const refresh = letGo && held.current && heldDistance.current >= HOME_PULL_REFRESH_DISTANCE
      held.current = false; heldDistance.current = 0; setSecret(null)
      Animated.timing(translate, { toValue: 0, duration: reducedRef.current ? 0 : 240, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start()
      if (refresh) void refreshRef.current().catch(error => reportError(error, { tags: { area: 'home-pull-refresh' } }))
    },
  }), [translate])
  const handlers = React.useMemo(() => PanResponder.create({
    onMoveShouldSetPanResponderCapture: (_event, gesture) => startsHomePull({ atTop: scrollTop.current <= 0, dx: gesture.dx, dy: gesture.dy, touches: gesture.numberActiveTouches }),
    onPanResponderGrant: () => pull.begin(),
    onPanResponderMove: (_event, gesture) => pull.move(homePullDistance(gesture.dy)),
    onPanResponderRelease: () => pull.end(true),
    onPanResponderTerminate: () => pull.end(false),
    onPanResponderTerminationRequest: () => false,
  }), [scrollTop, pull])

  // Web: a wheel or trackpad scroll up at the top is the desktop pull-to-refresh.
  React.useEffect(() => {
    if (Platform.OS !== 'web') return
    const node = root.current as unknown as HTMLElement | null
    if (!node?.addEventListener) return
    let state = IDLE_HOME_WHEEL
    let quiet: ReturnType<typeof setTimeout> | undefined
    const letGo = () => {
      if (state.held) pull.end(true)
      state = { ...IDLE_HOME_WHEEL, lastAt: state.lastAt }
    }
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return // pinch zoom, sideways scroll
      const before = state
      state = stepHomeWheel(state, { deltaY: event.deltaY, deltaMode: event.deltaMode, at: event.timeStamp, atTop: scrollTop.current <= 0 && webAtTop(event.target, node) })
      if (state.held && !before.held) pull.begin()
      if (state.held) { event.preventDefault(); pull.move(homePullDistance(state.drag)) }
      else if (before.held) pull.end(false)
      clearTimeout(quiet)
      quiet = setTimeout(letGo, HOME_WHEEL_END_MS)
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => { node.removeEventListener('wheel', onWheel); clearTimeout(quiet) }
  }, [scrollTop, pull])
  React.useEffect(() => () => { translate.stopAnimation() }, [translate])
  return <View ref={root} className="flex-1" style={{ flex: 1, overflow: 'hidden' }} onLayout={event => {
    const { width, height } = event.nativeEvent.layout
    if (width > 0 && height > 0) setBackdropHeight(Math.max(150, 400 * height / width))
  }} {...handlers.panHandlers}>
    {secret && <View pointerEvents="none" accessibilityLiveRegion="polite" testID="home-pull-secret" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#151b11' }}>
      <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}><SecretBackdrop kind={secret.backdrop} height={backdropHeight} /></View>
      <View style={{ paddingHorizontal: 24, height: 120, alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <Text style={{ fontSize: 32, lineHeight: 38 }}>{secret.emoji}</Text>
        <Text className="font-typed" style={{ color: '#e6e1c4', fontSize: 15, textAlign: 'center' }}>{secret.line}</Text>
      </View>
    </View>}
    {/* Inline flex keeps the animated wrapper filling the page, including empty lists; the inner
        View paints the page opaque (className is not applied to Animated.View), so the secret only
        shows in the gap the pull opens at the top. */}
    <Animated.View style={{ flex: 1, transform: [{ translateY: translate }] }}>
      <View className="flex-1 bg-canvas" style={{ flex: 1 }}>{children}</View>
    </Animated.View>
  </View>
}
